import type {
  OrganizationCustomRoleKey,
  OrganizationEnrollmentMode,
} from "@clerk/expo/types";
import * as ImagePicker from "expo-image-picker";
import { ImagePlus, Plus, Upload, X } from "lucide-react-native";
import { FC, useMemo, useState } from "react";
import { Image } from "react-native";

import {
  Box,
  DropdownPicker,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { NewOrgInput, useLocalization } from "@/hooks";
import { Region } from "@/types/resources";

import { Field, RegionLock, SwitchRow, TextField } from "./form";
import { formatDomains, isAllowedEmail, isValidEmail, slugify } from "./utils";

export type UserDraft = Record<
  "emailAddress" | "firstName" | "lastName",
  string
>;

export interface OrgDraft {
  credits: string;
  domains: NewOrgInput["domains"];
  imageUrl: string;
  name: string;
  seatLimit: string;
  slug: string;
  slugEdited: boolean;
  stripeCustomerId: string;
  unlimitedCredits: boolean;
}

export const EMPTY_ORG: OrgDraft = {
  credits: "",
  domains: [],
  imageUrl: "",
  name: "",
  seatLimit: "",
  slug: "",
  slugEdited: false,
  stripeCustomerId: "",
  unlimitedCredits: false,
};

export const EMPTY_USER: UserDraft = {
  emailAddress: "",
  firstName: "",
  lastName: "",
};

// =============================================================================
// Field groups
// =============================================================================
export const NewUserFields: FC<{
  domains?: string[];
  onChange: (value: UserDraft) => void;
  showErrors: boolean;
  value: UserDraft;
}> = ({ domains = [], onChange, showErrors, value }) => {
  const { i18n } = useLocalization();
  const errors = userDraftErrors(value, domains);
  const required = i18n.t("forms.required");

  return (
    <VStack className="gap-3">
      <HStack className="gap-3 flex-wrap">
        <Field
          error={showErrors && errors.firstName ? required : null}
          label={i18n.t("forms.user.firstName")}
        >
          <TextField
            invalid={showErrors && errors.firstName}
            onChangeText={(firstName) => onChange({ ...value, firstName })}
            placeholder={i18n.t("forms.placeholders.firstName")}
            value={value.firstName}
          />
        </Field>
        <Field
          error={showErrors && errors.lastName ? required : null}
          label={i18n.t("forms.user.lastName")}
        >
          <TextField
            invalid={showErrors && errors.lastName}
            onChangeText={(lastName) => onChange({ ...value, lastName })}
            placeholder={i18n.t("forms.placeholders.lastName")}
            value={value.lastName}
          />
        </Field>
      </HStack>
      <Field
        error={
          !showErrors || !errors.emailAddress
            ? null
            : isValidEmail(value.emailAddress)
              ? i18n.t("forms.user.emailDomain", {
                  domains: formatDomains(domains),
                })
              : i18n.t("forms.user.emailInvalid")
        }
        label={i18n.t("forms.user.email")}
      >
        <TextField
          invalid={showErrors && errors.emailAddress}
          keyboardType="email-address"
          onChangeText={(emailAddress) => onChange({ ...value, emailAddress })}
          placeholder={i18n.t("forms.placeholders.email")}
          value={value.emailAddress}
        />
      </Field>
    </VStack>
  );
};

export const OrgFields: FC<{
  onChange: (value: OrgDraft) => void;
  region: Region;
  showErrors: boolean;
  value: OrgDraft;
}> = ({ onChange, region, showErrors, value }) => {
  const { i18n } = useLocalization();
  const [domainInput, setDomainInput] = useState("");

  const enrollmentItems = useMemo(
    () => [
      {
        label: i18n.t("forms.org.enrollment.manualInvitation"),
        value: "manual_invitation",
      },
      {
        label: i18n.t("forms.org.enrollment.automaticSuggestion"),
        value: "automatic_suggestion",
      },
      {
        label: i18n.t("forms.org.enrollment.automaticInvitation"),
        value: "automatic_invitation",
      },
    ],
    [i18n]
  );

  const pickLogo = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      allowsEditing: true,
      aspect: [1, 1],
      mediaTypes: ["images"],
      quality: 0.8,
    });
    if (result.canceled || !result.assets[0]) return;
    onChange({ ...value, imageUrl: result.assets[0].uri });
  };

  const addDomain = () => {
    const name = domainInput.trim().toLowerCase().replace(/^@/, "");
    if (!name || value.domains.some((d) => d.name === name)) return;
    onChange({
      ...value,
      domains: [
        ...value.domains,
        { enrollmentMode: "manual_invitation", name },
      ],
    });
    setDomainInput("");
  };

  return (
    <VStack className="gap-4">
      <HStack className="gap-3 flex-wrap">
        <Field
          error={
            showErrors && !value.name.trim() ? i18n.t("forms.required") : null
          }
          label={i18n.t("forms.org.name")}
        >
          <TextField
            invalid={showErrors && !value.name.trim()}
            onChangeText={(name) =>
              onChange({
                ...value,
                name,
                slug: value.slugEdited ? value.slug : slugify(name),
              })
            }
            placeholder={i18n.t("forms.placeholders.orgName")}
            value={value.name}
          />
        </Field>
        <Field
          hint={i18n.t("forms.org.slugHint")}
          label={i18n.t("forms.org.slug")}
        >
          <TextField
            key={value.slugEdited ? "edited" : value.slug}
            onChangeText={(slug) =>
              onChange({ ...value, slug: slugify(slug), slugEdited: true })
            }
            placeholder={i18n.t("forms.placeholders.slug")}
            value={value.slug}
          />
        </Field>
      </HStack>

      <Field
        hint={i18n.t("forms.org.regionHint")}
        label={i18n.t("forms.org.region")}
      >
        <RegionLock region={region} />
      </Field>

      <Field
        hint={i18n.t("forms.org.logoHint")}
        label={i18n.t("forms.org.logo")}
        optional
      >
        <HStack className="items-center gap-3">
          <Pressable
            className="w-12 h-12 rounded-xl border border-dashed border-outline-300 bg-background-50 overflow-hidden items-center justify-center hover:bg-background-100"
            onPress={pickLogo}
          >
            {value.imageUrl ? (
              <Image
                source={{ uri: value.imageUrl }}
                style={{ height: 48, width: 48 }}
              />
            ) : (
              <ImagePlus color="#94a3b8" size={18} />
            )}
          </Pressable>
          <Pressable
            className="flex-row items-center gap-1.5 rounded-lg border border-outline-200 bg-white px-3 py-1.5 hover:bg-background-50"
            onPress={pickLogo}
          >
            <Upload color="#475569" size={14} />
            <Text className="text-xs font-medium text-typography-700">
              {value.imageUrl
                ? i18n.t("forms.org.logoReplace")
                : i18n.t("forms.org.logoUpload")}
            </Text>
          </Pressable>
          {!!value.imageUrl && (
            <Pressable
              className="rounded-lg px-2.5 py-1.5 hover:bg-background-50"
              onPress={() => onChange({ ...value, imageUrl: "" })}
            >
              <Text className="text-xs font-medium text-error-600">
                {i18n.t("forms.org.logoRemove")}
              </Text>
            </Pressable>
          )}
        </HStack>
      </Field>

      <Field
        error={
          showErrors && !value.domains.length
            ? i18n.t("forms.org.domainsRequired")
            : null
        }
        hint={i18n.t("forms.org.domainsHint")}
        label={i18n.t("forms.org.domains")}
      >
        <VStack className="gap-2">
          {value.domains.map((domain) => (
            <HStack className="items-center gap-2" key={domain.name}>
              <Box className="flex-1 rounded-xl border border-outline-200 bg-background-50 px-3 py-2.5">
                <Text className="text-sm text-typography-900">
                  @{domain.name}
                </Text>
              </Box>
              <DropdownPicker
                className="w-[220px]"
                isDisabled
                items={enrollmentItems}
                onChange={(mode) =>
                  onChange({
                    ...value,
                    domains: value.domains.map((d) =>
                      d.name === domain.name
                        ? {
                            ...d,
                            enrollmentMode: mode as OrganizationEnrollmentMode,
                          }
                        : d
                    ),
                  })
                }
                placeholder=""
                size="md"
                value={domain.enrollmentMode}
              />
              <Pressable
                className="p-2 rounded-lg hover:bg-background-100"
                onPress={() =>
                  onChange({
                    ...value,
                    domains: value.domains.filter(
                      (d) => d.name !== domain.name
                    ),
                  })
                }
              >
                <X color="#94a3b8" size={14} />
              </Pressable>
            </HStack>
          ))}
          <HStack className="items-center gap-2">
            <Box className="flex-1">
              <TextField
                autoFocus={value.domains.length > 0}
                key={value.domains.length}
                onChangeText={setDomainInput}
                onSubmitEditing={addDomain}
                placeholder={i18n.t("forms.placeholders.domain")}
                value={domainInput}
              />
            </Box>
            <Pressable
              className="flex-row items-center gap-1.5 rounded-xl border border-outline-200 px-3 py-2.5 hover:bg-background-50"
              onPress={addDomain}
            >
              <Plus color="#475569" size={14} />
              <Text className="text-sm font-medium text-typography-700">
                {i18n.t("forms.org.addDomain")}
              </Text>
            </Pressable>
          </HStack>
        </VStack>
      </Field>

      <HStack className="gap-3 flex-wrap">
        <Field
          hint={i18n.t("forms.org.seatLimitHint")}
          label={i18n.t("forms.org.seatLimit")}
          optional
        >
          <TextField
            keyboardType="numeric"
            onChangeText={(seatLimit) =>
              onChange({ ...value, seatLimit: seatLimit.replace(/\D/g, "") })
            }
            placeholder="25"
            value={value.seatLimit}
          />
        </Field>
        {!value.unlimitedCredits && (
          <Field
            hint={i18n.t("forms.org.creditsHint")}
            label={i18n.t("forms.org.credits")}
            optional
          >
            <TextField
              keyboardType="numeric"
              onChangeText={(credits) =>
                onChange({ ...value, credits: credits.replace(/\D/g, "") })
              }
              placeholder="0"
              value={value.credits}
            />
          </Field>
        )}
      </HStack>
      <SwitchRow
        checked={value.unlimitedCredits}
        onChange={(unlimitedCredits) =>
          onChange({ ...value, unlimitedCredits })
        }
        title={i18n.t("forms.org.unlimitedCredits")}
      />

      <Field
        hint={i18n.t("forms.org.stripeCustomerIdHint")}
        label={i18n.t("forms.org.stripeCustomerId")}
        optional
      >
        <TextField
          onChangeText={(stripeCustomerId) =>
            onChange({ ...value, stripeCustomerId })
          }
          placeholder={i18n.t("forms.placeholders.stripeCustomerId")}
          value={value.stripeCustomerId}
        />
      </Field>
    </VStack>
  );
};

export const RolePicker: FC<{
  onChange: (role: OrganizationCustomRoleKey) => void;
  value: OrganizationCustomRoleKey;
}> = ({ onChange, value }) => {
  const { i18n } = useLocalization();
  const items = useMemo(
    () => [
      { label: i18n.t("accounts.roles.admin"), value: "org:admin" },
      { label: i18n.t("accounts.roles.member"), value: "org:member" },
    ],
    [i18n]
  );
  return (
    <DropdownPicker
      className="w-[130px]"
      items={items}
      onChange={onChange}
      placeholder=""
      size="md"
      value={value}
    />
  );
};

// =============================================================================
// Validation
// =============================================================================
export function orgDraftToInput(draft: OrgDraft): NewOrgInput {
  return {
    credits: draft.unlimitedCredits ? 0 : Number(draft.credits || 0),
    domains: draft.domains,
    imageUrl: draft.imageUrl.trim(),
    maxAllowedMemberships: Number(draft.seatLimit || 0),
    name: draft.name.trim(),
    slug: draft.slug || slugify(draft.name),
    stripeCustomerId: draft.stripeCustomerId.trim() || null,
  };
}

export function userDraftErrors(
  draft: UserDraft,
  domains: string[] = []
): Record<keyof UserDraft, boolean> {
  return {
    emailAddress:
      !isValidEmail(draft.emailAddress) ||
      !isAllowedEmail(draft.emailAddress, domains),
    firstName: !draft.firstName.trim(),
    lastName: !draft.lastName.trim(),
  };
}

export function userDraftValid(
  draft: UserDraft,
  domains: string[] = []
): boolean {
  return !Object.values(userDraftErrors(draft, domains)).some(Boolean);
}
